import { Download } from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface ExportButtonProps {
  entity: "accounts" | "sites" | "kitchens";
}

export function ExportButton({ entity }: ExportButtonProps) {
  const headers = useApiHeaders();
  const { t } = useTranslation();

  async function download(format: "csv" | "json") {
    try {
      const res = await fetch(`${API_URL}/api/v1/${entity}/export?format=${format}`, { headers });
      if (!res.ok) throw new Error("Export failed");

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entity}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("common.exportFailed"));
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline">
          <Download className="mr-1 size-4" />
          {t("common.export")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => download("csv")}>
          {t("common.exportAsCsv")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => download("json")}>
          {t("common.exportAsJson")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
